import { Link } from "react-router-dom";
import { ShoppingCart, Menu, X, Lock, LogOut, PlusCircle, Users, Heart, Sparkles, Crown, Package, Baby } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { BRAND_LOGO } from "../../config/constants";

export function Navbar({ onMenuClick, menuOpen }) {
  const { isAdmin, logout } = useAuth();
  const { cart } = useCart();
  const cartCount = cart.length;
  
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-red-600/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="logo">
            <img src={BRAND_LOGO} alt="Fresh Po'Boy" className="h-14 w-auto" />
          </Link>
          
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-white/80 hover:text-cyan-400 transition-colors text-sm tracking-wide">Home</Link>
            <Link to="/new-drops" className="relative text-amber-300 hover:text-amber-200 transition-colors text-sm tracking-wide font-bold flex items-center gap-1.5" data-testid="new-drops-nav-link">
              <Sparkles size={14} className="animate-pulse" /> New Drop
              <span className="absolute -top-1 -right-3 w-1.5 h-1.5 bg-red-500 rounded-full animate-ping" />
            </Link>
            <Link to="/streetwear/ladies/money-queen" className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-amber-300 to-yellow-400 hover:from-yellow-200 hover:to-amber-200 transition-colors text-sm tracking-wide font-bold flex items-center gap-1.5" data-testid="money-queen-nav-link">
              <Crown size={14} className="text-amber-300" /> Money Queen
            </Link>
            <Link to="/shop" className="text-white/80 hover:text-cyan-400 transition-colors text-sm tracking-wide">Shop</Link>
            <Link to="/our-story" className="text-white/80 hover:text-cyan-400 transition-colors text-sm tracking-wide">Our Story</Link>
            <Link to="/lifestyle" className="text-white/80 hover:text-cyan-400 transition-colors text-sm tracking-wide">Lifestyle</Link>
            <Link to="/blackhistory" className="text-amber-400 hover:text-amber-300 transition-colors text-sm tracking-wide font-semibold">Black History</Link>
            <Link to="/hiphop" className="text-red-500 hover:text-red-400 transition-colors text-sm tracking-wide font-semibold">Hip Hop</Link>
            <Link to="/faith" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm tracking-wide font-semibold">Faith</Link>
            <Link to="/sports" className="text-red-500 hover:text-red-400 transition-colors text-sm tracking-wide font-semibold">Sports</Link>
            <Link to="/streetwear" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm tracking-wide font-semibold">Streetwear</Link>
            <Link to="/hoodies" className="text-red-500 hover:text-red-400 transition-colors text-sm tracking-wide font-semibold">Hoodies</Link>
            <Link to="/anime" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm tracking-wide font-semibold">Anime</Link>
            <Link to="/children" className="text-cyan-300 hover:text-pink-300 transition-colors text-sm tracking-wide font-semibold flex items-center gap-1" data-testid="children-nav-link">
              <Baby size={14} /> Kids
            </Link>
            <Link to="/adore" className="text-pink-400 hover:text-pink-300 transition-colors text-sm tracking-wide font-semibold flex items-center gap-1" data-testid="adore-nav-link">
              <Heart size={14} fill="currentColor" /> Adore Rae
            </Link>
            <Link to="/blog" className="text-white/80 hover:text-cyan-400 transition-colors text-sm tracking-wide" data-testid="blog-nav-link">Blog</Link>
            {isAdmin && (
              <>
                <Link to="/admin/add-product" className="text-green-400 hover:text-green-300 transition-colors text-sm tracking-wide font-semibold flex items-center gap-1">
                  <PlusCircle size={16} /> Add Product
                </Link>
                <Link to="/admin/subscribers" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm tracking-wide font-semibold flex items-center gap-1" data-testid="admin-subscribers-link">
                  <Users size={16} /> Subscribers
                </Link>
                <Link to="/admin/orders" className="text-amber-400 hover:text-amber-300 transition-colors text-sm tracking-wide font-semibold flex items-center gap-1" data-testid="admin-orders-link">
                  <Package size={16} /> Orders
                </Link>
              </>
            )}
          </div>

          <div className="flex items-center gap-4">
            {isAdmin ? (
              <button onClick={logout} className="p-2 text-green-400 hover:text-green-300" title="Logout" data-testid="logout-btn">
                <LogOut size={20} />
              </button>
            ) : (
              <Link to="/admin/login" className="p-2 text-white/60 hover:text-white" title="Admin" data-testid="admin-login-link">
                <Lock size={20} />
              </Link>
            )}
            <Link to="/cart" className="relative p-2 text-white/80 hover:text-white" data-testid="cart-link">
              <ShoppingCart size={22} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-amber-500 text-black text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
            <button onClick={onMenuClick} className="md:hidden p-2 text-white" data-testid="mobile-menu-btn">
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-black border-t border-white/10 px-4 py-4 space-y-3">
          <Link to="/" className="block text-white/80 py-2" onClick={onMenuClick}>Home</Link>
          <Link to="/new-drops" className="block text-amber-300 py-2 font-bold flex items-center gap-2" onClick={onMenuClick}><Sparkles size={14} /> New Drop</Link>
          <Link to="/streetwear/ladies/money-queen" className="block py-2 font-bold flex items-center gap-2 text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-amber-300 to-yellow-400" onClick={onMenuClick}><Crown size={14} className="text-amber-300" /> Money Queen</Link>
          <Link to="/shop" className="block text-white/80 py-2" onClick={onMenuClick}>Shop</Link>
          <Link to="/our-story" className="block text-white/80 py-2" onClick={onMenuClick}>Our Story</Link>
          <Link to="/lifestyle" className="block text-white/80 py-2" onClick={onMenuClick}>Lifestyle</Link>
          <Link to="/blackhistory" className="block text-amber-400 py-2 font-semibold" onClick={onMenuClick}>Black History</Link>
          <Link to="/hiphop" className="block text-red-400 py-2 font-semibold" onClick={onMenuClick}>Hip Hop</Link>
          <Link to="/faith" className="block text-cyan-400 py-2 font-semibold" onClick={onMenuClick}>Faith</Link>
          <Link to="/streetwear" className="block text-purple-400 py-2 font-semibold" onClick={onMenuClick}>Streetwear</Link>
          <Link to="/hoodies" className="block text-cyan-400 py-2 font-semibold" onClick={onMenuClick}>Hoodies</Link>
          <Link to="/anime" className="block text-red-400 py-2 font-semibold" onClick={onMenuClick}>Anime</Link>
          <Link to="/children" className="block text-cyan-300 py-2 font-semibold flex items-center gap-2" onClick={onMenuClick} data-testid="mobile-children-link"><Baby size={14} /> Kids</Link>
          <Link to="/sports" className="block text-orange-400 py-2 font-semibold" onClick={onMenuClick}>Sports</Link>
          <Link to="/adore" className="block text-pink-400 py-2 font-semibold flex items-center gap-2" onClick={onMenuClick}><Heart size={14} fill="currentColor" /> Adore Rae</Link>
          <Link to="/blog" className="block text-white/80 py-2" onClick={onMenuClick}>Blog</Link>
          {isAdmin && <Link to="/admin/add-product" className="block text-green-400 py-2" onClick={onMenuClick}>Add Product</Link>}
          {isAdmin && <Link to="/admin/subscribers" className="block text-cyan-400 py-2" onClick={onMenuClick}>Subscribers</Link>}
          {isAdmin && <Link to="/admin/orders" className="block text-amber-400 py-2" onClick={onMenuClick}>Orders</Link>}
        </div>
      )}
    </nav>
  );
}
